import React, { useMemo, useState, createElement } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import MetricCard from '../components/MetricCard';
import SegmentDetailDrawer from '../components/drawers/SegmentDetailDrawer';
import { useAdminData } from '../contexts/AdminDataContext';
import { Users, Activity, DollarSign, TrendingUp, Download, TrendingDown } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
type PeriodFilter = '7days' | '30days' | '90days' | 'thisMonth';
const PlatformAnalytics = () => {
  const {
    platformAnalytics,
    showToast
  } = useAdminData();
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('30days');
  const [selectedSegment, setSelectedSegment] = useState<any>(null);
  const [showSegmentDrawer, setShowSegmentDrawer] = useState(false);
  // Filter data based on period
  const filteredData = useMemo(() => {
    const today = new Date();
    let daysToShow = 30;
    switch (periodFilter) {
      case '7days':
        daysToShow = 7;
        break;
      case '30days':
        daysToShow = 30;
        break;
      case '90days':
        daysToShow = 90;
        break;
      case 'thisMonth':
        daysToShow = today.getDate();
        break;
    }
    const cutoffDate = new Date(today);
    cutoffDate.setDate(cutoffDate.getDate() - daysToShow);
    return {
      usersSeries: platformAnalytics.usersSeries.filter(item => new Date(item.date) >= cutoffDate),
      revenueSeries: platformAnalytics.revenueSeries.filter(item => new Date(item.date) >= cutoffDate)
    };
  }, [periodFilter, platformAnalytics]);
  const handleExportReport = () => {
    // Mock CSV export
    const csvContent = [['Date', 'Users', 'Revenue'], ...filteredData.usersSeries.map((item, idx) => [item.date, item.value, filteredData.revenueSeries[idx]?.value || 0])].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `platform-analytics-${periodFilter}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    showToast('Report esportato con successo!');
  };
  const handleSegmentClick = (segment: any) => {
    setSelectedSegment(segment);
    setShowSegmentDrawer(true);
  };
  const handleCloseSegmentDrawer = () => {
    setShowSegmentDrawer(false);
    setTimeout(() => setSelectedSegment(null), 300);
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-2961">
      <div className="w-20 flex-shrink-0" data-id="element-2962">
        <Sidebar data-id="element-2963" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-2964">
        <Topbar data-id="element-2965" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-2966">
          {/* Header with filters */}
          <div className="flex items-center justify-between mb-6" data-id="element-2967">
            <div data-id="element-2968">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-2969">
                Analitiche Piattaforma
              </h1>
              <p className="text-sm text-gray-500 mt-1" data-id="element-2970">
                Panoramica completa delle performance della piattaforma
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-2971">
              <select value={periodFilter} onChange={e => setPeriodFilter(e.target.value as PeriodFilter)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-600" data-id="element-2972">
                <option value="7days" data-id="element-2973">Ultimi 7 giorni</option>
                <option value="30days" data-id="element-2974">Ultimi 30 giorni</option>
                <option value="90days" data-id="element-2975">Ultimi 90 giorni</option>
                <option value="thisMonth" data-id="element-2976">Questo mese</option>
              </select>
              <button onClick={handleExportReport} className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors" data-id="element-2977">
                <Download className="h-4 w-4 mr-2" data-id="element-2978" />
                Esporta Report
              </button>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2979">
            <MetricCard title="Utenti Totali" value={platformAnalytics.kpis.totalUsers.toLocaleString()} change="12.5%" isPositive={true} icon={Users} data-id="element-2980" />
            <MetricCard title="Engagement Rate" value={`${platformAnalytics.kpis.engagementRate}%`} change="8.3%" isPositive={true} icon={Activity} data-id="element-2981" />
            <MetricCard title="Revenue" value={`€${platformAnalytics.kpis.revenue.toLocaleString()}`} change="15.7%" isPositive={true} icon={DollarSign} data-id="element-2982" />
            <MetricCard title="Retention" value={`${platformAnalytics.kpis.retention}%`} change="3.2%" isPositive={true} icon={TrendingUp} data-id="element-2983" />
          </div>

          {/* Charts Row */}
          <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-2984">
            {/* User Growth Chart */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm" data-id="element-2985">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-2986">
                Crescita Utenti
              </h3>
              <div className="h-80" data-id="element-2987">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2988">
                  <LineChart data={filteredData.usersSeries} data-id="element-2989">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-2990" />
                    <XAxis dataKey="date" stroke="#6B7280" fontSize={12} tickFormatter={value => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }} data-id="element-2991" />
                    <YAxis stroke="#6B7280" fontSize={12} data-id="element-2992" />
                    <Tooltip contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.5rem'
                  }} labelFormatter={value => {
                    const date = new Date(value);
                    return date.toLocaleDateString('it-IT');
                  }} formatter={(value: any) => [value.toLocaleString(), 'Utenti']} data-id="element-2993" />
                    <Line type="monotone" dataKey="value" stroke="#108d3e" strokeWidth={2} dot={false} name="Utenti" data-id="element-2994" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Revenue Trend Chart */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm" data-id="element-2995">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-2996">
                Trend Revenue
              </h3>
              <div className="h-80" data-id="element-2997">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2998">
                  <AreaChart data={filteredData.revenueSeries} data-id="element-2999">
                    <defs data-id="element-3000">
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1" data-id="element-3001">
                        <stop offset="5%" stopColor="#108d3e" stopOpacity={0.3} data-id="element-3002" />
                        <stop offset="95%" stopColor="#108d3e" stopOpacity={0} data-id="element-3003" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-3004" />
                    <XAxis dataKey="date" stroke="#6B7280" fontSize={12} tickFormatter={value => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }} data-id="element-3005" />
                    <YAxis stroke="#6B7280" fontSize={12} data-id="element-3006" />
                    <Tooltip contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '0.5rem'
                  }} labelFormatter={value => {
                    const date = new Date(value);
                    return date.toLocaleDateString('it-IT');
                  }} formatter={(value: any) => [`€${value.toLocaleString()}`, 'Revenue']} data-id="element-3007" />
                    <Area type="monotone" dataKey="value" stroke="#108d3e" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" name="Revenue" data-id="element-3008" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Segment Breakdown Table */}
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden" data-id="element-3009">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-3010">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-3011">
                Breakdown per Segmento
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-3012">
                Analisi dettagliata per canale di acquisizione
              </p>
            </div>
            <div className="overflow-x-auto" data-id="element-3013">
              <table className="w-full" data-id="element-3014">
                <thead data-id="element-3015">
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3016">
                    <th className="px-6 py-3" data-id="element-3017">Segmento</th>
                    <th className="px-6 py-3" data-id="element-3018">Utenti</th>
                    <th className="px-6 py-3" data-id="element-3019">Engagement</th>
                    <th className="px-6 py-3" data-id="element-3020">Revenue</th>
                    <th className="px-6 py-3" data-id="element-3021">Trend</th>
                    <th className="px-6 py-3 text-right" data-id="element-3022">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-3023">
                  {platformAnalytics.segments.map(segment => <tr key={segment.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleSegmentClick(segment)} data-id="element-3024">
                      <td className="px-6 py-4" data-id="element-3025">
                        <div className="font-medium text-gray-900" data-id="element-3026">
                          {segment.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-3027">
                        {segment.users.toLocaleString()}
                      </td>
                      <td className="px-6 py-4" data-id="element-3028">
                        <div className="flex items-center" data-id="element-3029">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2 max-w-[100px]" data-id="element-3030">
                            <div className="bg-green-600 h-2 rounded-full" style={{
                          width: `${segment.engagement}%`
                        }} data-id="element-3031" />
                          </div>
                          <span className="text-sm font-medium text-gray-900" data-id="element-3032">
                            {segment.engagement}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-3033">
                        €{segment.revenue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4" data-id="element-3034">
                        <div className="flex items-center" data-id="element-3035">
                          {segment.trend > 0 ? <TrendingUp className="h-4 w-4 text-green-600 mr-1" data-id="element-3036" /> : <TrendingDown className="h-4 w-4 text-red-600 mr-1" data-id="element-3037" />}
                          <span className={`text-sm font-medium ${segment.trend > 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-3038">
                            {segment.trend > 0 ? '+' : ''}
                            {segment.trend}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-3039">
                        <button onClick={e => {
                      e.stopPropagation();
                      handleSegmentClick(segment);
                    }} className="text-green-600 hover:text-green-700 font-medium text-sm" data-id="element-3040">
                          Dettagli
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* Segment Detail Drawer */}
      <SegmentDetailDrawer isOpen={showSegmentDrawer} onClose={handleCloseSegmentDrawer} segment={selectedSegment} data-id="element-3041" />
    </div>;
};
export default PlatformAnalytics;